import { createInitialState, PaletteStorageName, State, Tools } from '@/store/state'
import { MutationTree } from 'vuex'
import { Point } from '@/domain/models/Point'
import { TrinketPoint } from '@/domain/models/TrinketPoint'
import { v4 as uuid } from 'uuid'
import { ToastOptions } from '@/components/Common/ToastOptions'
import { TrinketLine } from '@/domain/models/TrinketLine'
import { calculateDistanceBetweenPointAndSegment, isValidColor } from '@/domain/Utils'
import { TrinketText } from '@/domain/models/TrinketText'
import { GridConfiguration } from '@/domain/models/TrinketSettings'

export interface Mutations {
    closeSettings (state: State): void

    configurationUpdateGridConfiguration (state: State, payload: Partial<GridConfiguration>): void

    configurationUpdateAutoSave (state: State, payload: { active: boolean, timeInterval: number, interval: number }): void

    configurationUpdateSaveOnExit (state: State, saveOnExit: boolean): void

    configurationUpdateContinueLine (state: State, continueLine: boolean): void

    createToast (state: State, payload: ToastOptions): void

    loadPalette (state: State, palette: string[]): void

    newTrinket (state: State): void

    openSettings (state: State): void

    paletteAddColor (state: State, color: string): void

    removeAutoSave (state: State): void

    saveToFile (state: State, payload: { fileName: string; type: string; data: string }): void

    saveToLocalStorage (state: State, payload: { key: string; data: string }): void

    trinketAddLine (state: State, point: Point): void

    trinketAddPoint (state: State, point: Point): void

    trinketAddText (state: State, point: Point): void

    trinketCancelLine (state: State): void

    trinketLoad (state: State, trinketGrid: State): void

    trinketPickColor (state: State, point: TrinketPoint): void

    trinketRemoveLine (state: State, point: Point): void

    trinketRemovePoint (state: State, point: Point): void

    trinketRemoveText (state: State, text: TrinketText): void

    trinketSelectTool (state: State, tool: Tools): void

    trinketSetColor (state: State, payload: { color: string; zone: 'fill' | 'stroke' | 'lineStroke' }): void

    trinketUpdateLine (state: State, point: Point): void

    trinketUpdateText (state: State, text: TrinketText): void
}

export const mutations: MutationTree<State> & Mutations = {
    closeSettings (state: State): void {
        state.settingsModal.show = false
    },
    configurationUpdateGridConfiguration (state: State, payload: Partial<GridConfiguration>): void {
        state.configuration.grid.width = payload.width ?? state.configuration.grid.width
        state.configuration.grid.height = payload.height ?? state.configuration.grid.height
        state.configuration.grid.gridHelperColor = payload.gridHelperColor ?? state.configuration.grid.gridHelperColor
        state.configuration.grid.showGridHelper = payload.showGridHelper ?? state.configuration.grid.showGridHelper
        state.configuration.grid.orientation = payload.orientation ?? state.configuration.grid.orientation
        state.configuration.grid.showGridAxis = payload.showGridAxis ?? state.configuration.grid.showGridAxis
        state.configuration.grid.gridAxisColor = payload.gridAxisColor ?? state.configuration.grid.gridAxisColor

        // Update point radius
        state.configuration.point.radius = state.configuration.grid.width - state.configuration.point.margin
    },
    configurationUpdateAutoSave (state: State, payload: { active: boolean, timeInterval: number, interval: number }): void {
        state.configuration.saving.autoSave.active = payload.active
        state.configuration.saving.autoSave.timeInterval = payload.timeInterval
        state.saveInterval = payload.interval
    },
    configurationUpdateSaveOnExit (state: State, saveOnExit: boolean): void {
        state.configuration.saving.saveOnExit = saveOnExit
    },
    configurationUpdateContinueLine (state: State, continueLine: boolean): void {
        state.configuration.line.continueLine = continueLine
    },
    createToast (state: State, options: ToastOptions): void {
        state.toast = options
    },
    loadPalette (state: State, palette: string[]): void {
        state.palette = palette
    },
    newTrinket (state: State): void {
        state.trinketGrid = createInitialState()
    },
    openSettings (state: State): void {
        state.settingsModal.show = true
    },
    paletteAddColor (state: State, color: string): void {
        if (isValidColor(color) && !state.palette.find(c => c === color)) {
            state.palette.push(color)

            localStorage.setItem(PaletteStorageName, JSON.stringify(state.palette))
        }
    },
    removeAutoSave (state: State): void {
        state.configuration.saving.autoSave.active = false
        state.saveInterval = undefined
    },
    saveToFile (state: State, {
        fileName,
        data,
        type
    }: { fileName: string; type: string; data: string }): void {
        // Create blob and url with serialized data
        const blob = new Blob([data], { type })
        const url = URL.createObjectURL(blob)
        // Create dynamic link and click to it to being download
        const aTag = document.createElement('a')

        document.body.appendChild(aTag)
        aTag.download = fileName
        aTag.href = url
        aTag.click()

        // Remove link and clear blob to prevent memory overflow and duplicated streams
        document.body.removeChild(aTag)
        URL.revokeObjectURL(url)
    },
    saveToLocalStorage (state: State, {
        key,
        data
    }: { key: string, data: string }): void {
        localStorage.setItem(key, data)
    },
    trinketAddLine (state: State, {
        x,
        y
    }: Point): void {
        if (!state.trinketGrid.currentLine) {
            state.trinketGrid.currentLine = {
                x,
                y,
                x2: x,
                y2: y,
                stroke: state.trinketGrid.colors.lineStroke,
                uuid: uuid()
            }
        } else {
            const currentLine: TrinketLine = { ...state.trinketGrid.currentLine }
            currentLine.x2 = x
            currentLine.y2 = y

            if (currentLine.x !== currentLine.x2 || currentLine.y !== currentLine.y2) {
                state.trinketGrid.lines.push(currentLine)
            }

            if (state.configuration.line.continueLine) {
                state.trinketGrid.currentLine = {
                    x,
                    y,
                    x2: x,
                    y2: y,
                    stroke: state.trinketGrid.currentLine.stroke,
                    uuid: uuid()
                }
            } else {
                state.trinketGrid.currentLine = undefined
            }
        }
    },
    trinketAddPoint (state: State, {
        x,
        y
    }: Point): void {
        // Get point in current grid position
        const currentPoint = state.trinketGrid.points.find(point => point.x === x && point.y === y)

        // Prevent duplicating points at the same location
        // Just update colors
        if (currentPoint) {
            switch (state.trinketGrid.selectedTool) {
                case Tools.FILLER:
                    currentPoint.fill = state.trinketGrid.colors.fill
                    break
                case Tools.BORDER:
                    currentPoint.stroke = state.trinketGrid.colors.stroke
                    break
            }
        } else { // Create new point and store it
            const point: TrinketPoint = {
                x,
                y,
                fill: 'transparent',
                stroke: 'transparent',
                uuid: uuid()
            }

            switch (state.trinketGrid.selectedTool) {
                case Tools.FILLER:
                    point.fill = state.trinketGrid.colors.fill
                    break
                case Tools.BORDER:
                    point.stroke = state.trinketGrid.colors.stroke
                    break
            }

            state.trinketGrid.points.push(point)
        }
    },
    trinketAddText (state: State, {
        x,
        y
    }: Point): void {
        if (state.trinketGrid.texts.find(text => text.x === x && text.y === y)) {
            // text exists
        }

        state.trinketGrid.texts.push({
            x,
            y,
            color: '#000000',
            text: 'Texto',
            fontSize: 16,
            uuid: uuid()
        })
    },
    trinketCancelLine (state: State): void {
        state.trinketGrid.currentLine = undefined
    },
    trinketLoad (state: State, trinketGrid: State): void {
        state.trinketGrid = trinketGrid.trinketGrid
        state.configuration = trinketGrid.configuration
        state.palette = trinketGrid.palette
    },
    trinketPickColor (state: State, {
        stroke,
        fill
    }: TrinketPoint): void {
        if (fill !== 'transparent') {
            state.trinketGrid.colors.fill = fill
        }

        if (stroke !== 'transparent') {
            state.trinketGrid.colors.stroke = stroke
        }
    },
    trinketRemoveLine (state: State, point: Point): void {
        const currentLineIndex = state.trinketGrid.lines.findIndex(line => calculateDistanceBetweenPointAndSegment(point, line) <= 1)

        if (currentLineIndex >= 0) {
            state.trinketGrid.lines.splice(currentLineIndex, 1)
        }
    },
    trinketRemovePoint (state: State, point: Point): void {
        const index: number = state.trinketGrid.points.findIndex(({
            x,
            y
        }) => x === point.x && y === point.y)
        if (index >= 0) {
            state.trinketGrid.points.splice(index, 1)
        }
    },
    trinketRemoveText (state: State, text: TrinketText): void {
        const currentTextIndex = state.trinketGrid.texts.findIndex(({ uuid }) => text.uuid === uuid)

        if (currentTextIndex >= 0) {
            state.trinketGrid.texts.splice(currentTextIndex, 1)
        }
    },
    trinketSelectTool (state: State, tool: Tools): void {
        if (state.trinketGrid.currentLine) {
            state.trinketGrid.currentLine = undefined
        }

        state.trinketGrid.selectedTool = tool
    },
    trinketSetColor (state: State, {
        color,
        zone
    }): void {
        state.trinketGrid.colors[zone] = color
    },
    trinketUpdateLine (state: State, point: Point): void {
        if (state.trinketGrid.currentLine) {
            state.trinketGrid.currentLine.x2 = point.x
            state.trinketGrid.currentLine.y2 = point.y
        }
    },
    trinketUpdateText (state: State, text: TrinketText): void {
        const stateText = state.trinketGrid.texts.find(({ uuid }) => text.uuid === uuid)

        if (stateText) {
            stateText.text = text.text
            stateText.color = text.color
            stateText.fontSize = text.fontSize
        }
    }
}
